import {announce} from '@github-ui/aria-live'
import {isLoggedIn} from '@github-ui/client-env'
import {useAlive} from '@github-ui/use-alive'
import {BellIcon, BellSlashIcon} from '@primer/octicons-react'
import {ActionList, IconButton} from '@primer/react'
import {Tooltip} from '@primer/react/next'
import {useCallback, useEffect, useRef, useTransition} from 'react'
import {graphql, useFragment, useRefetchableFragment, useRelayEnvironment} from 'react-relay'

import {ALIVE_REASONS} from '../../../constants/alive'
import {LABELS} from '../../../constants/labels'
import {commitUpdateIssueSubscriptionMutation} from '../../../mutations/update-issue-subscription'
import type {StickySubscribeButtonFragment$key} from './__generated__/StickySubscribeButtonFragment.graphql'
import type {StickySubscribeButtonRefetchableFragment$key} from './__generated__/StickySubscribeButtonRefetchableFragment.graphql'

export type StickySubscribeButtonProps = {
  issue: StickySubscribeButtonFragment$key & StickySubscribeButtonRefetchableFragment$key
  /**
   * Render variant. Defaults to `'bell'` (icon button used in the sticky header).
   * Use `'menu-item'` to render the action as an ActionList.Item inside an overflow menu.
   */
  variant?: 'bell' | 'menu-item'
}

export function StickySubscribeButton({issue, variant = 'bell'}: StickySubscribeButtonProps) {
  const {threadSubscriptionChannel, id} = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment StickySubscribeButtonFragment on Issue {
        id
        threadSubscriptionChannel
      }
    `,
    issue,
  )

  if (!isLoggedIn() || !threadSubscriptionChannel) return null

  return (
    <StickySubscribeButtonInternal
      issue={issue}
      id={id}
      threadSubscriptionChannel={threadSubscriptionChannel}
      variant={variant}
    />
  )
}

function StickySubscribeButtonInternal({
  id,
  issue,
  threadSubscriptionChannel,
  variant,
}: {
  id: string
  issue: StickySubscribeButtonRefetchableFragment$key
  threadSubscriptionChannel: string
  variant: 'bell' | 'menu-item'
}) {
  const environment = useRelayEnvironment()
  const [, startTransition] = useTransition()
  const shouldUpdateAnnouncement = useRef(false)

  const [data, refetch] = useRefetchableFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment StickySubscribeButtonRefetchableFragment on Issue
      @refetchable(queryName: "StickySubscribeButtonRefetchableFragmentQuery") {
        viewerThreadSubscriptionFormAction
      }
    `,
    issue,
  )
  const subscribed = data.viewerThreadSubscriptionFormAction === 'UNSUBSCRIBE'

  const announceSubscriptionUpdates = useCallback((newSubscribed: boolean, success = true) => {
    if (success) {
      announce(
        newSubscribed ? LABELS.notifications.subscribedAnnouncement : LABELS.notifications.unsubscribedAnnouncement,
      )
    } else {
      announce(LABELS.somethingWentWrong)
    }
  }, [])

  useEffect(() => {
    if (shouldUpdateAnnouncement.current) {
      announceSubscriptionUpdates(subscribed)
      shouldUpdateAnnouncement.current = false
    }
  }, [subscribed, announceSubscriptionUpdates])

  const refreshSubscription = useCallback(() => {
    startTransition(() => {
      refetch(
        {},
        {
          fetchPolicy: 'network-only',
          onComplete: () => {
            shouldUpdateAnnouncement.current = true
          },
        },
      )
    })
  }, [refetch, startTransition])

  useAlive(threadSubscriptionChannel, (event: {reason: string}) => {
    if (!event?.reason) return
    if (event.reason === ALIVE_REASONS.SUBSCRIBED && !subscribed) {
      refreshSubscription()
    } else if (event.reason === ALIVE_REASONS.UNSUBSCRIBED && subscribed) {
      refreshSubscription()
    }
  })

  const toggleSubscription = useCallback(() => {
    const newSubscribed = !subscribed
    commitUpdateIssueSubscriptionMutation({
      environment,
      input: {
        subscribableId: id,
        state: newSubscribed ? 'SUBSCRIBED' : 'UNSUBSCRIBED',
      },
      onCompleted: () => announceSubscriptionUpdates(newSubscribed),
      onError: () => announceSubscriptionUpdates(subscribed, false),
    })
  }, [environment, id, subscribed, announceSubscriptionUpdates])

  const menuItemLabel = subscribed ? LABELS.notifications.subscribedButton : LABELS.notifications.unsubscribedButton
  const tooltipText = subscribed
    ? `${LABELS.notifications.subscribedDescription} Click to unsubscribe`
    : `${LABELS.notifications.unsubscribedDescription} Click to subscribe`

  if (variant === 'menu-item') {
    return (
      <ActionList.Item onSelect={toggleSubscription}>
        <ActionList.LeadingVisual>{subscribed ? <BellIcon /> : <BellSlashIcon />}</ActionList.LeadingVisual>
        {menuItemLabel}
      </ActionList.Item>
    )
  }

  // Use a static aria-label with aria-pressed so screen readers announce the
  // toggle state once (e.g. "Notifications, toggle button, pressed") rather
  // than the confusing "Unsubscribe, toggle button, pressed" combination that
  // results from changing the label and the pressed state together.
  // See WAI-ARIA APG toggle button guidance.
  return (
    <Tooltip type="description" text={tooltipText} direction="s">
      <IconButton
        icon={subscribed ? BellIcon : BellSlashIcon}
        aria-label={LABELS.notifications.notificationsTitle}
        aria-pressed={subscribed}
        variant="invisible"
        onClick={toggleSubscription}
      />
    </Tooltip>
  )
}
